import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import { GridRenderCellParams, GridColDef } from '@mui/x-data-grid-premium';
import { FakeButtonLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useSearchParamsState } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { cleanOrderExecutions, fetchOrderExecutions } from '../../redux/modules/orderExecution/orderExecution.actions';
import { orderExecutionByRequestIdSelector } from '../../redux/modules/orderExecution/orderExecution.selectors';
import { ReducerState } from '../../redux/store.types';
import { locations } from '../../routes/locations';
import { PortfolioOrderExecutionStatus } from '../../types/portfolioOrder';
import { parseJSON } from '../../utils/json';

const orderExecutionStatusToInfo: {
  [prop in PortfolioOrderExecutionStatus]: {
    color: 'error' | 'info' | 'success' | 'warning';
    icon: React.ElementType;
  };
} = {
  [PortfolioOrderExecutionStatus.EXECUTION]: {
    color: 'info',
    icon: InfoIcon,
  },
  [PortfolioOrderExecutionStatus.APPROVED]: {
    color: 'success',
    icon: CheckCircleIcon,
  },
  [PortfolioOrderExecutionStatus.IN_REVIEW]: {
    color: 'warning',
    icon: WarningIcon,
  },
  [PortfolioOrderExecutionStatus.RECONCILIATION]: {
    color: 'warning',
    icon: WarningIcon,
  },
};

type OrderExecutionTableProps = {
  executionId: string;
};

export function OrderExecutionTable({ executionId }: OrderExecutionTableProps) {
  const requestId = `order-executions-list-by-execution-id-${executionId}`;
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const [columns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 80,
    },
    {
      field: 'executionId',
      headerName: 'Execution Id',
      type: 'string',
      flex: 1,
    },
    {
      field: 'orderId',
      headerName: 'OrderId',
      type: 'string',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.portfolioOrder(params.value)}>
            {params.value}
          </FakeButtonLink>
        );
      },
    },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(PortfolioOrderExecutionStatus),
      minWidth: 150,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = orderExecutionStatusToInfo[params.row?.status?.name as PortfolioOrderExecutionStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const { totalOrderExecutions, orderExecutions, fetchStatus } = useSelector((state: ReducerState) =>
    orderExecutionByRequestIdSelector(state, requestId),
  );
  const loading = fetchStatus === fetchStatuses.pending;
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '50');

  useEffect(
    () => () => {
      dispatch(cleanOrderExecutions());
    },
    [],
  );

  return (
    <>
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">Order Executions &nbsp;</Typography>
      </Box>
      <Table
        ref={tableRef}
        wrapperHeight={300}
        fetchItems={fetchOrderExecutions}
        rows={orderExecutions}
        columns={columns}
        loading={loading}
        disableColumnFilter
        disableColumnMenu
        density="compact"
        rowCount={totalOrderExecutions}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        fetchItemsCustomParams={{ requestId }}
        requiredFilters={[{ field: 'executionId', value: executionId, operator: 'is', id: 0 }]}
        initialColumnVisibilityModel={{ id: false, executionId: false }}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
      />
    </>
  );
}
